// Pure analytics policy core — no import.meta, DOM, storage, or network.
// Browser wiring lives in ./consent.ts and ./gtm.ts; this file owns the
// versioned consent record, exact Consent Mode values, URL sanitization, and
// the closed v1 event contract.

export const CONSENT_STORAGE_KEY = 'lw_privacy_consent_v1';
export const CONSENT_RECORD_VERSION = 1;

export type ConsentDecision = 'granted' | 'denied';
export type ConsentSignalState = 'granted' | 'denied';

export interface StoredConsentRecord {
  decision: ConsentDecision;
  version: typeof CONSENT_RECORD_VERSION;
  at?: string;
}

export interface ConsentSignals {
  analytics_storage: ConsentSignalState;
  ad_storage: 'denied';
  ad_user_data: 'denied';
  ad_personalization: 'denied';
}

export interface DefaultConsentSignals extends ConsentSignals {
  wait_for_update: 500;
}

const DENIED_SIGNALS: ConsentSignals = {
  analytics_storage: 'denied',
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied'
};

/** Exact four-signal Consent Mode v2 update. Advertising stays denied in v1. */
export function consentSignalsFor(decision: ConsentDecision): ConsentSignals {
  return {
    ...DENIED_SIGNALS,
    analytics_storage: decision === 'granted' ? 'granted' : 'denied'
  };
}

/** Default command payload; wait_for_update belongs only on the default. */
export function defaultConsentSignals(): DefaultConsentSignals {
  return {
    ...DENIED_SIGNALS,
    wait_for_update: 500
  };
}

/** Region/notice permission and explicit analytics permission are distinct. */
export function resolveConsentDecision(
  stored: ConsentDecision | null,
  globalPrivacyControl: boolean,
  noticeAnalyticsAllowed = false,
  explicitAnalyticsAllowed = false
): ConsentDecision {
  return globalPrivacyControl || stored === 'denied' || (!noticeAnalyticsAllowed && !explicitAnalyticsAllowed) ? 'denied' : 'granted';
}

/** Parse a stored record fail-closed. Old/malformed/unversioned data is absent. */
export function parseStoredConsent(raw: string | null): StoredConsentRecord | null {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

    const candidate = parsed as Record<string, unknown>;
    if (candidate.version !== CONSENT_RECORD_VERSION) return null;
    if (candidate.decision !== 'granted' && candidate.decision !== 'denied') return null;
    if (candidate.at !== undefined && typeof candidate.at !== 'string') return null;

    return {
      decision: candidate.decision,
      version: CONSENT_RECORD_VERSION,
      ...(typeof candidate.at === 'string' ? { at: candidate.at } : {})
    };
  } catch {
    return null;
  }
}

export interface RecruitingBookingParams {
  source_page: 'join' | 'join_marketing';
  topic: 'compensation' | 'scenario' | 'marketing';
}

export interface AnalyticsEventParamsMap {
  phone_click: { placement: 'footer' | 'footer_tollfree' };
  email_click: { placement: 'footer' };
  apply_modal_open: Record<string, never>;
  apply_outbound: { destination: 'existing_client' | 'new_client' };
  calculator_start: { calculator: 'mortgage' | 'affordability' | 'refinance' | 'buydown' | 'points' };
  calculator_action: { calculator: 'buydown' | 'points'; action: 'compare' | 'print' | 'contact' };
  mortgage_rate_entry: { placement: 'board' | 'card' | 'points_dialog'; action: 'quote' | 'callback' };
  calculator_complete: { calculator: 'mortgage' | 'affordability' | 'refinance' | 'buydown' | 'points' };
  recruiting_booking_open: RecruitingBookingParams;
  recruiting_booking_details: RecruitingBookingParams;
  recruiting_booking_confirmed: RecruitingBookingParams;
}

export type AnalyticsEventName = keyof AnalyticsEventParamsMap;
export type ParameterizedAnalyticsEventName = Exclude<AnalyticsEventName, 'apply_modal_open'>;

interface EventPolicy {
  readonly [param: string]: ReadonlySet<string>;
}

const CALCULATORS = new Set(['mortgage', 'affordability', 'refinance', 'buydown', 'points']);
const RECRUITING_BOOKING = {
  source_page: new Set(['join', 'join_marketing']),
  topic: new Set(['compensation', 'scenario', 'marketing'])
};

const EVENT_POLICIES: Record<AnalyticsEventName, EventPolicy> = {
  phone_click: { placement: new Set(['footer', 'footer_tollfree']) },
  email_click: { placement: new Set(['footer']) },
  apply_modal_open: {},
  apply_outbound: { destination: new Set(['existing_client', 'new_client']) },
  calculator_start: { calculator: CALCULATORS },
  calculator_complete: { calculator: CALCULATORS },
  calculator_action: { calculator: new Set(['buydown', 'points']), action: new Set(['compare', 'print', 'contact']) },
  mortgage_rate_entry: { placement: new Set(['board', 'card', 'points_dialog']), action: new Set(['quote', 'callback']) },
  recruiting_booking_open: RECRUITING_BOOKING,
  recruiting_booking_details: RECRUITING_BOOKING,
  recruiting_booking_confirmed: RECRUITING_BOOKING
};

export interface ValidatedAnalyticsEvent {
  event: AnalyticsEventName;
  params: Record<string, string>;
}

/**
 * Validate the complete event, not individual fields. Unknown/missing/extra
 * params, non-string values, and values outside the exact enum reject the
 * entire event so partial payloads cannot acquire misleading semantics.
 */
export function validateAnalyticsEvent(
  name: unknown,
  params: unknown = {}
): ValidatedAnalyticsEvent | null {
  if (
    typeof name !== 'string' ||
    !Object.prototype.hasOwnProperty.call(EVENT_POLICIES, name)
  ) return null;
  if (!params || typeof params !== 'object' || Array.isArray(params)) return null;

  const eventName = name as AnalyticsEventName;
  const policy = EVENT_POLICIES[eventName];
  const candidate = params as Record<string, unknown>;
  const expectedKeys = Object.keys(policy);
  const actualKeys = Object.keys(candidate);
  if (actualKeys.length !== expectedKeys.length) return null;

  const clean: Record<string, string> = {};
  for (const key of expectedKeys) {
    const value = candidate[key];
    if (typeof value !== 'string' || !policy[key].has(value)) return null;
    clean[key] = value;
  }

  return { event: eventName, params: clean };
}

/** Strip query and hash; the only URL shape allowed to leave the site. */
export function sanitizePath(pathname: string): string {
  return pathname.split(/[?#]/, 1)[0] || '/';
}

/** Detect Supabase OAuth state in either the query string or fragment. */
/**
 * True for every URL the Orion login treats as a Supabase OAuth callback
 * (isSupabaseAuthCallback), plus refresh tokens. Deliberately broader than that
 * detector, including a bare `error=`, because analytics fails closed.
 */
const AUTH_CALLBACK_KEYS = [
  'access_token', 'refresh_token', 'provider_token', 'provider_refresh_token',
  'code', 'error', 'error_code', 'error_description'
] as const;

export function hasAuthCallbackState(search: string, hash: string): boolean {
  // Same parsing as isSupabaseAuthCallback, so encoded (%63ode) and bare
  // (#provider_token) keys are recognized exactly as the Orion login sees them.
  const query = new URLSearchParams(search.replace(/^\?/, ''));
  const fragment = new URLSearchParams(hash.replace(/^#/, ''));
  return AUTH_CALLBACK_KEYS.some((key) => query.has(key) || fragment.has(key));
}
